import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, FormProvider } from "react-hook-form";
import {
  DateFilterTypesEnum,
  PaymentDueAccountVM,
  PaymentMethodVM,
  PaymentService
} from "@propertelligent/client-api";
import apiHelper from "../../utils/apiHelper";
import { NotificationType, PubSub, TopicsEnum } from "../../utils/pubsub";
import { UserLoginData } from "../../components/common/charts/Enums";
import FixedFooter from "../listings/add-listing-footer/fixed-footer";
import './payment.css';
import PaymentPayablesORReceivablesList from "./PaymentPayablesORReceivablesList";
import PaymentSummery from "./PaymentSummery";
import DateHelper from "../../utils/DateHelper";
import ReceivableDetailCard from "../listings/listing-cards/receivable-detail";
import Card from "../card";

interface ApiData {
  paymentTenantlist: PaymentDueAccountVM[];
  paymentDetail: PaymentDueAccountVM | null;
  paymentMethod: PaymentMethodVM[];
}

interface PaymentSelectionState {
  selectedPayments: PaymentDueAccountVM[];
  totalAmount: number;
}

interface PaymentFormData {
  PaymentModeId: string;
  checkNum: string;
  transactionId: string;
  date: string;
}

const INITIAL_API_DATA: ApiData = {
  paymentMethod: [],
  paymentTenantlist: [],
  paymentDetail: null
};

const DATE_FILTERS = {
  NEXT_30_DAYS: DateFilterTypesEnum.Next30Days,
  NEXT_3_MONTHS: DateFilterTypesEnum.Next3Months,
  NEXT_6_MONTHS: DateFilterTypesEnum.Next6Months,
};


const ReceivablesPostPayment = () => {
  const [fullyQualifiedFilter, setFullyQualifiedFilter] = useState("paidFlowTypeId == 2");
  const [dateFilterType, setDateFilterType] = useState<number | null>(null);
  const [data, setData] = useState<ApiData>(INITIAL_API_DATA);
  const [isLoading, setIsLoading] = useState(false);
  const [paymentState, setPaymentState] = useState<PaymentSelectionState>({
    selectedPayments: [],
    totalAmount: 0,
  });

  const navigate = useNavigate();

  const { id } = useParams();

  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));

  const fetchInitialData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [paymentMethod, paymentDetail] = await Promise.all([
        apiHelper(
          PaymentService.getApiPaymentMethodTenantlist,
          { showNotification: false },
          id
        ),
        apiHelper(
          PaymentService.getApiPaymentDueAccountReceivable,
          { showNotification: false },
          id
        ),
      ]);

      setData(prev => ({
        ...prev,
        paymentMethod: paymentMethod?.data?.success || [],
        paymentDetail: paymentDetail?.data?.success || null
      }));
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load required data",
        type: NotificationType.Failure,
      });
    } finally {
      setIsLoading(false);
    }
  }, [id, organizationId]);


  const fetchPaymentTenantlist = useCallback(async () => {
    try {

      const isDateFiltered = [DATE_FILTERS.NEXT_30_DAYS, DATE_FILTERS.NEXT_3_MONTHS, DATE_FILTERS.NEXT_6_MONTHS].includes(dateFilterType as number);

      const paymentTenantlist = await apiHelper(
        PaymentService.getApiPaymentDueAccountTenantlist,
        { showNotification: false },
        id,
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        fullyQualifiedFilter,
        undefined,
        isDateFiltered ? dateFilterType : undefined,
        isDateFiltered ? DateHelper.getClientDate() : undefined,
        undefined,
        undefined,
        isDateFiltered ? "DueDate" : undefined
      );
      setData(prev => ({
        ...prev,
        paymentTenantlist: paymentTenantlist?.data?.success || []
      }));

      return paymentTenantlist;
    } catch (error) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Failed to load payment payee list",
        type: NotificationType.Failure,
      });
      throw error;
    } finally {

    }
  }, [dateFilterType, fullyQualifiedFilter]);


  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchPaymentTenantlist();
  }, [fetchPaymentTenantlist]);

  const handleSetSelectedPayments = useCallback((payments: PaymentDueAccountVM[], totalAmount: number) => {
    setPaymentState({
      selectedPayments: payments,
      totalAmount,
    });
  }, []);

  const methods = useForm<PaymentFormData>({
    defaultValues: {
      PaymentModeId: "",
      checkNum: "",
      transactionId: "",
      date: new Date().toISOString().split('T')[0]
    }
  });


  const onCancel = useCallback(() => navigate(-1), [navigate]);
  const onSubmit = async (formData: PaymentFormData) => {



    if (paymentState.selectedPayments.length === 0) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: "Please select at least one payment",
        type: NotificationType.Warning,
      });
      return;
    }



    const transactionId = formData?.transactionId || crypto.randomUUID().replaceAll('-', '').toUpperCase();

    try {
      const paymentPromises = paymentState.selectedPayments.map(item => {

        const payload = {
          payDueAccountId: Number(item?.id),
          amount: Number(item.amount),
          fromPaymentMethodId: Number(formData?.PaymentModeId),
          checkNumber: formData?.checkNum,
          paymentDate: new Date(formData.date).toISOString(),
          transactionId: transactionId,
        };

        const paymentMethod = data.paymentMethod.find(x => x.id === Number(formData?.PaymentModeId))?.accountName;
        if (paymentMethod !== "Check" && paymentMethod !== "Cash") {
          delete payload.checkNumber;
        }

        return apiHelper(
          PaymentService.putApiPaymentDueAccountPay,
          {
            successMessage: "Payment settled successfully",
            failureMessage: "Unable to settle payment",
            showNotification: true,
          },
          payload
        );
      });

      await Promise.all(paymentPromises);
      methods.reset();
      navigate(-1);
    } catch (error: any) {
      PubSub.publish(TopicsEnum.Notification.toString(), {
        message: error?.message,
        type: NotificationType.Warning,
      });
    }
  };

  return (
    <div className="payment-container d-flex flex-column gap-3 full-page-content">
      <ReceivableDetailCard
        Id="PayableDetailCard"
        data={data.paymentDetail}
        loading={isLoading}
        IsFooter={true}
        onRefresh={fetchInitialData}
      />
      {
        data.paymentDetail && (
          <>
            <Card className="no-bg p-0">
              <div className="row align-items-center mx-3 my-3">
                <div className="col-md-6">
                  <PaymentPayablesORReceivablesList
                    paymentMode="Receivable"
                    payments={data.paymentTenantlist}
                    selectedPayments={paymentState.selectedPayments}
                    setSelectedPayments={handleSetSelectedPayments}
                    setDateFilterType={setDateFilterType}
                    setFullyQualifiedFilter={setFullyQualifiedFilter}
                    DATE_FILTERS={DATE_FILTERS}
                  />
                </div>
                <div className="col-md-6">
                  <div className="d-flex flex-column gap-3">
                    <FormProvider {...methods}>
                      <PaymentSummery
                        Payments={paymentState.selectedPayments}
                        totalAmount={String(paymentState.totalAmount)}
                        paymentMethods={data.paymentMethod}
                      />
                    </FormProvider>
                  </div>
                </div>
              </div>
            </Card>

            <FixedFooter
              onSubmit={methods.handleSubmit(onSubmit)}
              loading={isLoading}
              buttonLabel="Post Payment"
              onCancel={onCancel}
            />
          </>
        )
      }

    </div>
  );
};

export default ReceivablesPostPayment;